'use client';

import { useEffect, useRef } from 'react';

/**
 * Lightweight sticky-scroll hook for bounded containers (e.g. max-h-[400px]).
 * Auto-scrolls to bottom when `trigger` changes, but only if the user
 * hasn't manually scrolled up. Re-engages when user scrolls back to bottom.
 *
 * Uses direct `scrollTop` assignment — suited for internal scrollable areas
 * where a sentinel `<div>` approach is unnecessary.
 *
 * @param containerRef  Ref to the scrollable container element
 * @param trigger       Dependency that triggers a scroll attempt (e.g. content length)
 * @param threshold     Distance from bottom (px) within which user is considered "at bottom"
 */
export function useStickToBottom(containerRef: React.RefObject<HTMLElement | null>, trigger: unknown, threshold = 50) {
	const isAtBottomRef = useRef(true);

	// Track scroll position with passive listener
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const handleScroll = () => {
			isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
		};

		el.addEventListener('scroll', handleScroll, { passive: true });
		return () => el.removeEventListener('scroll', handleScroll);
	}, [containerRef, threshold]);

	// Auto-scroll only when user is at bottom
	useEffect(() => {
		const el = containerRef.current;
		if (el && isAtBottomRef.current) {
			el.scrollTop = el.scrollHeight;
		}
	}, [containerRef, trigger]);
}
